import {
  ERR_CANNOT_DELETE_STOP_WITH_EVENTS,
  ERR_DELETED_EXTRA_CO2_TYPES,
  ERR_OUTDATED_CARGO_REVISION,
  convertStopUnitsDecimalToString,
} from "dora-shared";
import axios, { getData } from "../../../axios";
import {
  dropDeletedExtraCo2TypesParam,
  isDeletedExtraCo2TypesError,
} from "../../data/extra-co2-types";
import { isAxiosError } from "axios";
import {
  createSlice,
  createAction,
  PayloadAction,
  createAsyncThunk,
} from "@reduxjs/toolkit";
import {
  CargoFormData,
  decodeCargo,
  editCargoOutputT,
  editCargoInputT,
  postCargoWithEquipmentT,
  postCargoWithInvoiceLinesAndEquipmentT,
  createCargoOnDriverRoutesReqT,
  CargoData,
} from "./types";
import { draftLinesCleared } from "../../data/draft-invoice-lines";
import { loadDocumentsForCargo as loadStopDocumentsForCargo } from "../../data/stop-documents";
import { draftEquipmentCleared } from "../../data/draft-cargo-equipment";
import {
  cargoSharedToStatusT,
  CargoSharedToStatus,
  cargoSharedFromStatusT,
  copyCargoToDriverRoutesStartResT,
  copyCargoToDriverRoutesJobT,
  createStopUnitTypes,
  decimal,
  cargoAlreadyDeletedErrorT,
  CargoAlreadyDeletedError,
  ERR_CARGO_ALREADY_DELETED,
  routeToArchiveSuggestionT,
} from "dora-contracts";

import { cloneDeep, omit } from "lodash";
import {
  apiCargoToFormData,
  defaultFormData,
  formDataStopToApiStop,
} from "./helpers";
import { notifyL } from "../../notifications";
import * as t from "io-ts";
import { AppDispatch, GetState } from "../../../redux-store";
import { createErrorReportingAsyncThunk } from "../../helpers";
import * as tPromise from "io-ts-promise";
import { selectCargoView } from "../../data/cargo-views/selectors";
import { deliveryT } from "../../data/deliveries/types";
import { deliveryUpdated } from "../../data/deliveries";
import { CargoFormDataBase } from "../cargo-and-template-shared";
import {
  clearSelectedTemplate,
  loadAndUseClientCargoTemplate,
  loadCurrentTemplateForCargo,
} from "../../data/client-order-templates";
import { DateTime } from "luxon";
import { selectNewShipmentDateAdjustment } from "../../data/org-settings/selectors";
import { selectRemoveDropoffByDefault } from "../../data/user-settings/selectors";
import { nanoid } from "nanoid";
import {
  CargoDeletedBy,
  selectIsCargoDeleted,
  selectOpenCargoId,
} from "./selectors";

const { stopUniteTemplatesT } = createStopUnitTypes({ decimal });

export type CargoFromApi = t.TypeOf<typeof editCargoOutputT>;

const prefix = "app/cargo-dialog";

type CargoDialogStatus = "CREATE" | "EDIT" | "CLOSED";

interface CargoDialogState {
  status: CargoDialogStatus;
  initialFormData?: CargoFormData;
  resetFormData?: CargoFormData | CargoFormDataBase;
  previousFormData?: CargoFormData | CargoFormDataBase;
  // True when initialFormData was restored from previousFormData (i.e. the
  // modal was re-opened from redux-cached data, not freshly loaded/fetched).
  // That cached data may reference stops that were deleted elsewhere in the
  // meantime, so consumers use this to tolerate a stale-stop-reference error.
  initialFormDataFromCache: boolean;
  isSaving: boolean;
  loadedDataRevisionNumber: string | null;
  // requestId of the most recently dispatched loadCargo. Only that load may
  // fill the dialog: a slower, earlier load for another cargo would otherwise
  // land last and save one cargo's form under the other's id (DORA-390).
  latestLoadCargoRequestId: string | null;
  // The cargo loaded for editing; only meaningful while status is "EDIT".
  editingCargoId: string | null;
  // A cargo the dialog found deleted by someone else while it was open (on
  // save, or when re-checking it after a reconnect), and who deleted it. The
  // dialog then shows a banner and refuses to save it (DORA-458).
  deletedCargo: ({ cargoId: string } & CargoDeletedBy) | null;
  // Someone else's change to the open cargo clashes with the user's unsaved
  // edits: the dialog stops taking in changes and shows a banner, and a save
  // shows the outdated-revision dialog (DORA-441).
  remoteConflict: boolean;
  sharedToStatus: CargoSharedToStatus;
}

const initialSharedToState = {
  status: "NONE" as const,
  subcontractorOrgName: null,
  contractId: null,
};

const initialState: CargoDialogState = {
  status: "CLOSED",
  initialFormDataFromCache: false,
  isSaving: false,
  loadedDataRevisionNumber: null,
  latestLoadCargoRequestId: null,
  editingCargoId: null,
  deletedCargo: null,
  remoteConflict: false,
  sharedToStatus: initialSharedToState,
};

export const cargoDeleted = createAction<{
  cargoId: string;
  routeId: string | null;
}>(`${prefix}/cargo-deleted`);

// The coded 404 body for a cargo that's already deleted, or null for any other
// error.
const cargoAlreadyDeletedError = (e: unknown) =>
  isAxiosError(e) &&
  e.response?.status === 404 &&
  cargoAlreadyDeletedErrorT.is(e.response.data)
    ? e.response.data
    : null;

const isCargoAlreadyDeletedError = (e: unknown) =>
  cargoAlreadyDeletedError(e) !== null;

// The cargo open in the dialog turned out to be deleted elsewhere (on save, or
// after a reconnect): the dialog flags it, and the board drops it as it does
// for the socket's cargo-view.deleted (see the cargo-views and dispatching
// slices).
export const openCargoFoundDeleted = createAction<
  { cargoId: string } & CargoDeletedBy
>(`${prefix}/open-cargo-found-deleted`);

const markOpenCargoDeleted = (
  dispatch: AppDispatch,
  getState: GetState,
  cargoId: string,
  error: CargoAlreadyDeletedError
) => {
  const routeId = selectCargoView(cargoId)(getState())?.assignedRouteId ?? null;
  dispatch(
    openCargoFoundDeleted({
      cargoId,
      deletedBy: error.deletedBy ?? null,
      deletedByDriver: error.deletedByDriver ?? null,
    })
  );
  dispatch(cargoDeleted({ cargoId, routeId }));
};

// For the dialog's loads that follow the cargo itself (splits, documents,
// events): they can race a delete by another dispatcher (DORA-489). True when
// e is the coded "already deleted" 404, so the caller resolves instead of
// failing; if it's the open cargo the dialog is flagged deleted too, in case
// the socket's cargo-view.deleted hasn't arrived (or was missed).
export const handleCargoDeletedWhileLoading = (
  e: unknown,
  thunkAPI: { dispatch: AppDispatch; getState: GetState },
  cargoId: string
): boolean => {
  const deletedError = cargoAlreadyDeletedError(e);
  if (!deletedError) {
    return false;
  }
  const state = thunkAPI.getState();
  if (
    selectOpenCargoId(state) === cargoId &&
    !selectIsCargoDeleted(cargoId)(state)
  ) {
    markOpenCargoDeleted(
      thunkAPI.dispatch,
      thunkAPI.getState,
      cargoId,
      deletedError
    );
  }
  return true;
};

// After a reconnect (e.g. the laptop slept) the socket's cargo-view.deleted
// may have been missed, so ask the server whether the open cargo still exists.
export const recheckOpenCargo = createErrorReportingAsyncThunk(
  `${prefix}/recheck-open-cargo`,
  async (_: void, thunkAPI) => {
    const cargoId = selectOpenCargoId(thunkAPI.getState());
    if (!cargoId || selectIsCargoDeleted(cargoId)(thunkAPI.getState())) {
      return;
    }
    try {
      await axios.get(`/api/cargos/${cargoId}`);
    } catch (e) {
      const deletedError = cargoAlreadyDeletedError(e);
      if (!deletedError) {
        throw e;
      }
      markOpenCargoDeleted(
        thunkAPI.dispatch,
        thunkAPI.getState,
        cargoId,
        deletedError
      );
    }
  },
  // A background check: a failure shouldn't toast over the user's work.
  { skipNotification: true }
);

// The open cargo now stands at this revision: the dialog took in someone
// else's change, or saved its own.
export const openCargoRevisionAdopted = createAction<string>(
  `${prefix}/open-cargo-revision-adopted`
);
export const openCargoRemoteConflict = createAction(
  `${prefix}/open-cargo-remote-conflict`
);

// Someone else changed the open cargo (the server nudged the dialog, or a save
// came back outdated): fetch the latest version for the dialog to merge into
// its form. Unlike loadCargo it leaves the dialog's state alone; null when the
// cargo has been deleted meanwhile, which marks it deleted like recheckOpenCargo.
export const fetchLatestOpenCargo = createErrorReportingAsyncThunk(
  `${prefix}/fetch-latest-open-cargo`,
  async (
    cargoId: string,
    thunkAPI
  ): Promise<{ data: CargoData; revision: string } | null> => {
    try {
      const decoded = await axios
        .get(`/api/cargos/${cargoId}`)
        .then(getData)
        .then(decodeCargo);
      return { data: apiCargoToFormData(decoded), revision: decoded.revision };
    } catch (e) {
      const deletedError = cargoAlreadyDeletedError(e);
      if (!deletedError) {
        throw e;
      }
      markOpenCargoDeleted(
        thunkAPI.dispatch,
        thunkAPI.getState,
        cargoId,
        deletedError
      );
      return null;
    }
  },
  // A background fetch: a failure shouldn't toast over the user's work.
  { skipNotification: true }
);

export const deleteCargo = createErrorReportingAsyncThunk(
  `${prefix}/delete`,
  async (cargoId: string, thunkApi) => {
    const routeId = selectCargoView(cargoId)(
      thunkApi.getState()
    )?.assignedRouteId;
    try {
      await axios.delete<CargoFormData>(`/api/cargos/${cargoId}`);
    } catch (e) {
      // Deleted meanwhile from elsewhere (another tab, another dispatcher):
      // what the user asked for has already happened, so finish as a delete.
      // Any other 404 on the way (e.g. its route) is still a failure.
      if (!isCargoAlreadyDeletedError(e)) {
        throw e;
      }
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "shipmentAlreadyDeleted",
          type: "info",
        })
      );
    }
    thunkApi.dispatch({
      type: "DELETE_CARGO",
      payload: { cargoId, routeId },
    });
    thunkApi.dispatch(cargoDeleted({ cargoId, routeId }));
  }
);

// Starts the async copy job and returns its id; progress is polled separately.
export const startCopyCargoToDriverRoutes = createErrorReportingAsyncThunk(
  `${prefix}/start-copy-to-driver-routes`,
  async (input: {
    cargoId: string;
    driverIds: string[];
    date: string;
    copyInvoiceLines: boolean;
    resetInvoiceLineQuantities: boolean;
    dropDeletedExtraCo2Types?: boolean;
  }) => {
    return axios
      .post(
        `/api/cargos/${input.cargoId}/copy-to-driver-routes`,
        {
          driverIds: input.driverIds,
          date: input.date,
          copyInvoiceLines: input.copyInvoiceLines,
          resetInvoiceLineQuantities: input.resetInvoiceLineQuantities,
        },
        { params: dropDeletedExtraCo2TypesParam(input.dropDeletedExtraCo2Types) }
      )
      .then(getData)
      .then(tPromise.decode(copyCargoToDriverRoutesStartResT))
      .catch(refuseDeletedExtraCo2Types);
  }
);

// Copying lines whose extra CO2 type was deleted is refused until the user
// agrees to copy without them. Returned, not thrown, so it isn't reported as
// an error; the dialog asks and resends with the flag.
const refuseDeletedExtraCo2Types = (err: unknown) => {
  if (isDeletedExtraCo2TypesError(err)) {
    return { refused: ERR_DELETED_EXTRA_CO2_TYPES } as const;
  }
  throw err;
};

export const fetchCopyCargoToDriverRoutesJob = createErrorReportingAsyncThunk(
  `${prefix}/fetch-copy-to-driver-routes-job`,
  async (jobId: string) => {
    return axios
      .get(`/api/cargos/copy-to-driver-routes-jobs/${jobId}`)
      .then(getData)
      .then(tPromise.decode(copyCargoToDriverRoutesJobT));
  }
);

// Only decides whether to offer the optional "archive this route?" prompt after
// a save, so a failure skips the prompt without the generic error toast.
export const loadRouteToArchiveSuggestion = createErrorReportingAsyncThunk(
  `${prefix}/load-route-to-archive-suggestion`,
  async (cargoId: string) =>
    axios
      .get(`/api/cargos/${cargoId}/should-suggest-archive-route`)
      .then(getData)
      .then(tPromise.decode(routeToArchiveSuggestionT)),
  { skipNotification: true }
);

export const loadCargo = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (
    cargoId: string,
    thunkAPI
  ): Promise<{
    cargoId: string;
    data: CargoFormData & { revision: string };
  } | null> => {
    try {
      const decoded = await axios
        .get(`/api/cargos/${cargoId}`)
        .then(getData)
        .then(decodeCargo);
      let clientCargoTemplateId = decoded.clientCargoTemplateId;
      if (clientCargoTemplateId) {
        const templateResult = await thunkAPI.dispatch(
          loadCurrentTemplateForCargo(clientCargoTemplateId)
        );
        // The template has been deleted since this cargo was created from it:
        // drop the stale reference so it isn't sent back on save/duplicate.
        if (
          loadCurrentTemplateForCargo.fulfilled.match(templateResult) &&
          templateResult.payload === null
        ) {
          clientCargoTemplateId = null;
        }
      } else {
        thunkAPI.dispatch(clearSelectedTemplate());
      }
      const data = apiCargoToFormData({ ...decoded, clientCargoTemplateId });
      // Before load.fulfilled renders the stops, so their documents lists find
      // the load in flight instead of each firing its own request.
      thunkAPI.dispatch(
        loadStopDocumentsForCargo({
          cargoId,
          stopIds: [...decoded.pickupList, ...decoded.dropoffList].map(
            (stop) => stop.id
          ),
        })
      );
      return { cargoId, data: data as any };
    } catch (e) {
      // Deleted meanwhile (e.g. the dialog reopened from a reload or another
      // tab): not a failure, there's just nothing to show. Resolves with null
      // so the dialog closes; any other 404 is still a failure.
      if (isCargoAlreadyDeletedError(e)) {
        thunkAPI.dispatch(
          notifyL({
            namespace: "notifications",
            key: "shipmentNoLongerExists",
            type: "info",
          })
        );
        return null;
      }
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key: "loadingCargoFailed",
          type: "error",
        })
      );
      throw e;
    }
  }
);

export const loadCopyOfCargo = createErrorReportingAsyncThunk(
  `${prefix}/load-cargo-copy`,
  async (cargoId: string, thunkAPI) => {
    try {
      const initialFormData = await axios
        .get<unknown>(`/api/cargos/${cargoId}`)
        .then(getData)
        .then(tPromise.decode(editCargoOutputT))
        .then(apiCargoToFormData)
        .then((x) => omit(x, "id"))
        .then(createCargoCopy);
      const assignTeamId = thunkAPI.getState().auth.user?.defaultTeamId;
      const state = thunkAPI.getState();
      const newShipmentDateAdjustment = selectNewShipmentDateAdjustment(state);

      return {
        cargoId,
        initialFormData,
        resetFormData: defaultFormData({
          assignedTeamId: assignTeamId!,
          newShipmentDateAdjustment,
        }),
      };
    } catch (e) {
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key: "loadingCargoFailed",
          type: "error",
        })
      );
      throw e;
    }
  }
);

// Shapes a cargo form's values into the create/update request body. Shared by
// the normal submit flow and startCreateCargoOnDriverRoutes, which sends the
// same shape plus target drivers/date.
const buildCargoRequestBody = (
  formData: CargoData & { keepAlive?: boolean; submitType?: string }
) => {
  const actualFormData = omit(formData, ["submitType", "keepAlive", "createdAt"]);
  return {
    ...actualFormData,
    minTemperature: formData.minTemperature || null,
    maxTemperature: formData.maxTemperature || null,
    type: formData.type,
    clientId: formData.clientId || null,
    clientCargoTemplateId: formData.clientCargoTemplateId ?? null,
    pickupList: formData.pickupList.map(formDataStopToApiStop),
    dropoffList: formData.dropoffList.map(formDataStopToApiStop),
    stopUnits: formData.stopUnits.map(su => ({
      ...su,
      ...convertStopUnitsDecimalToString(su),
    })) || [],
  };
};

// Starts the async job that creates a brand new cargo directly on one or more
// drivers' routes in one request (as opposed to startCopyCargoToDriverRoutes,
// which copies an already-persisted cargo). Progress is polled the same way,
// via fetchCopyCargoToDriverRoutesJob.
export const startCreateCargoOnDriverRoutes = createErrorReportingAsyncThunk(
  `${prefix}/start-create-on-driver-routes`,
  async (input: {
    formData: CargoData;
    driverIds: string[];
    date: string;
    invoiceLines: t.TypeOf<typeof createCargoOnDriverRoutesReqT>["invoiceLines"];
    dropDeletedExtraCo2Types?: boolean;
  }) => {
    const body = buildCargoRequestBody(input.formData);
    return axios
      .post(
        "/api/cargos/create-on-driver-routes",
        createCargoOnDriverRoutesReqT.encode({
          ...body,
          route_id: null,
          driverIds: input.driverIds,
          date: input.date,
          invoiceLines: input.invoiceLines,
        } as t.TypeOf<typeof createCargoOnDriverRoutesReqT>),
        { params: dropDeletedExtraCo2TypesParam(input.dropDeletedExtraCo2Types) }
      )
      .then(getData)
      .then(tPromise.decode(copyCargoToDriverRoutesStartResT))
      .catch(refuseDeletedExtraCo2Types);
  }
);

export const submit = createErrorReportingAsyncThunk(
  `${prefix}/submit`,
  async (
    formData: CargoData & {
      keepAlive?: boolean;
      routeId?: string;
      submitType: "CREATE" | "UPDATE" | "DUPLICATE" | "CREATE_AND_MAKE_COPY";
      deleteStopsWithEvents?: boolean;
      cargoIdToCopyInvoiceLinesFrom: string | null;
      cargoIdToCopyFilesFrom: string | null;
      resetInvoiceLineQuantities?: boolean;
      dropDeletedExtraCo2Types?: boolean;
    },
    thunkAPI
  ) => {
    const cargoDialogState = thunkAPI.getState().app.cargoDialog;
    const actualFormData = omit(formData, [
      "submitType",
      "keepAlive",
      "createdAt",
      "dropDeletedExtraCo2Types",
    ]);
    // Adding lines whose extra CO2 type was deleted is refused until the
    // user agrees to save without them; the dialog asks and resubmits.
    const deletedExtraCo2TypesRefusal = (err: unknown) =>
      isDeletedExtraCo2TypesError(err)
        ? {
            requestFormData: actualFormData,
            keepAlive: formData.keepAlive,
            submitType: formData.submitType,
            error: ERR_DELETED_EXTRA_CO2_TYPES,
          }
        : null;
    const dropDeletedParam = formData.dropDeletedExtraCo2Types
      ? "dropDeletedExtraCo2Types=true"
      : null;
    // const cargoId = selectCargoId(thunkAPI.getState());
    const cargoId = formData.id;
    const routeId = formData.routeId;
    const body = buildCargoRequestBody(formData);
    let data;
    if (formData.submitType === "UPDATE") {
      try {
        let url = `/api/cargos/${cargoId}?revisionNumber=${cargoDialogState.loadedDataRevisionNumber}`;
        if (formData.deleteStopsWithEvents) {
          url += "&deleteStopsWithEvents=true";
        }
        if (dropDeletedParam) {
          url += `&${dropDeletedParam}`;
        }
        const result = await axios
          .put(url, editCargoInputT.encode({ ...body, ref: formData.ref! }))
          .then(getData)
          .then(
            tPromise.decode(
              t.strict({ delivery: deliveryT, cargoRevision: t.string })
            )
          );
        thunkAPI.dispatch(deliveryUpdated(result.delivery));
        // The next save from a dialog kept open goes against this revision.
        thunkAPI.dispatch(openCargoRevisionAdopted(result.cargoRevision));
        thunkAPI.dispatch(
          notifyL({
            namespace: "notifications",
            key: "shipmentSaved",
            type: "success",
          })
        );
      } catch (err: any) {
        const deletedTypesRefusal = deletedExtraCo2TypesRefusal(err);
        if (deletedTypesRefusal) {
          return deletedTypesRefusal;
        }
        if (
          err?.response?.status === 409 &&
          err?.response?.data?.message === ERR_CANNOT_DELETE_STOP_WITH_EVENTS
        ) {
          return {
            requestFormData: actualFormData,
            keepAlive: formData.keepAlive,
            submitType: formData.submitType,
            error: ERR_CANNOT_DELETE_STOP_WITH_EVENTS,
          };
        }
        if (
          err?.response?.status === 409 &&
          err?.response?.data?.message === ERR_OUTDATED_CARGO_REVISION
        ) {
          return {
            requestFormData: actualFormData,
            keepAlive: formData.keepAlive,
            submitType: formData.submitType,
            error: ERR_OUTDATED_CARGO_REVISION,
          };
        }
        // Deleted by someone else while the dialog was open (DORA-458).
        const deletedError = cargoAlreadyDeletedError(err);
        if (deletedError && cargoId) {
          markOpenCargoDeleted(
            thunkAPI.dispatch,
            thunkAPI.getState,
            cargoId,
            deletedError
          );
          return {
            requestFormData: actualFormData,
            keepAlive: formData.keepAlive,
            submitType: formData.submitType,
            error: ERR_CARGO_ALREADY_DELETED,
          };
        }
        throw err;
      }
    } else {
      const state = thunkAPI.getState();
      // Equipment only exists as a draft while creating. A duplicate is made
      // from a saved order and does not take its equipment along.
      const draftEquipment =
        formData.submitType === "DUPLICATE"
          ? []
          : state.data.draftCargoEquipment.picks;
      // Invoice lines come from the in-memory draft (the selected template's
      // lines were loaded there). Files, if copied from a template, are handled
      // by the new endpoint via a query param.
      const useInvoiceLinesEndpoint =
        formData.submitType === "CREATE" &&
        !formData.cargoIdToCopyInvoiceLinesFrom;
      const postCargo = async (url: string, payload: unknown) => {
        try {
          return { data: await axios.post(url, payload), refusal: null };
        } catch (err) {
          const refusal = deletedExtraCo2TypesRefusal(err);
          if (refusal) {
            return { data: null, refusal };
          }
          throw err;
        }
      };
      if (useInvoiceLinesEndpoint) {
        const draftLines = state.data.draftInvoiceLines.lines;
        const urlParams = [
          formData.cargoIdToCopyFilesFrom &&
            `copyFilesFromCargoId=${formData.cargoIdToCopyFilesFrom}`,
          dropDeletedParam,
        ].filter(Boolean);
        const url = urlParams.length
          ? `/api/cargos/with-invoice-lines?${urlParams.join("&")}`
          : "/api/cargos/with-invoice-lines";
        const posted = await postCargo(
          url,
          postCargoWithInvoiceLinesAndEquipmentT.encode({
            ...body,
            route_id: routeId || null,
            invoiceLines: draftLines.map((l) => ({
              productId: l.productId,
              unitId: l.unitId,
              description: l.description,
              quantity: l.quantity,
              amount: l.amount,
              discountPercentage: l.discountPercentage,
              percentage: l.percentage,
              isFromRoadToll: l.isFromRoadToll,
            })),
            equipment: draftEquipment,
          })
        );
        if (posted.refusal) {
          // Nothing was created: keep the draft lines for the resubmit.
          return posted.refusal;
        }
        data = posted.data;
        thunkAPI.dispatch(draftLinesCleared());
      } else {
        let url = "/api/cargos";
        const urlParams: string[] = [];
        if (formData.cargoIdToCopyInvoiceLinesFrom) {
          urlParams.push(
            `copyInvoiceLinesFromCargoId=${formData.cargoIdToCopyInvoiceLinesFrom}`
          );
        }
        if (formData.cargoIdToCopyFilesFrom) {
          urlParams.push(
            `copyFilesFromCargoId=${formData.cargoIdToCopyFilesFrom}`
          );
        }
        if (formData.resetInvoiceLineQuantities) {
          urlParams.push("resetInvoiceLineQuantities=true");
        }
        if (dropDeletedParam) {
          urlParams.push(dropDeletedParam);
        }
        if (urlParams.length) {
          url += `?${urlParams.join("&")}`;
        }
        const posted = await postCargo(
          url,
          postCargoWithEquipmentT.encode({
            ...body,
            route_id: routeId || null,
            equipment: draftEquipment,
          })
        );
        if (posted.refusal) {
          return posted.refusal;
        }
        data = posted.data;
      }
      // The picks now belong to the created order, and a copy made next must
      // not try to reserve the same units again.
      thunkAPI.dispatch(draftEquipmentCleared());
      thunkAPI.dispatch(
        notifyL({
          namespace: "notifications",
          key:
            formData.submitType !== "DUPLICATE"
              ? "shipmentCreated"
              : // The original is gone, so it was copied without saving it.
                cargoId && selectIsCargoDeleted(cargoId)(thunkAPI.getState())
                ? "shipmentDuplicated"
                : "shipmentSavedAndDuplicated",
          type: "success",
        })
      );
    }
    return {
      ...(data && {
        id: data?.data.id,
      }),
      requestFormData: actualFormData,
      keepAlive: formData.keepAlive,
      submitType: formData.submitType,
      error: undefined,
    };
  }
);

// The submitted data still shares nested objects (e.g. each stop's `place`)
// with react-hook-form's internal values, and the dialog stays open on them.
// Immer freezes whatever lands in state, so without the deep clone the next
// edit of a stop's place in the form throws "Cannot assign to read only
// property" (Sentry DISPATCHER-C).
const createCargoCopy = (formData: CargoFormData): CargoFormDataBase => {
  const data = cloneDeep(formData);
  return {
    ...omit(data, "ref"),
    // A copy starts its own invoicing life, no matter the original's status
    invoiceStatus: "OPEN",
    pickupList: data.pickupList.map((stop) => ({
      ...stop,
      id: undefined,
      provisoryId: nanoid(), // Generate temporary ID for stopUnit references
      cargo_id: null,
    })),
    dropoffList: data.dropoffList.map((stop) => ({
      ...stop,
      id: undefined,
      provisoryId: nanoid(), // Generate temporary ID for stopUnit references
      cargo_id: null,
    })),
  };
};

export const createNewCargo =
  (liteVersionEnabled?: boolean) =>
  (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const previousFormData = state.app.cargoDialog.previousFormData;
    const assignTeamId = state.auth.user?.defaultTeamId;
    const newShipmentDateAdjustment = selectNewShipmentDateAdjustment(state);
    const removeDropoffByDefault = selectRemoveDropoffByDefault(state);
    const defaultData = defaultFormData(
      {
        assignedTeamId: assignTeamId!,
        newShipmentDateAdjustment,
        removeDropoffByDefault,
      },
      liteVersionEnabled
    );
    const initialFormData = previousFormData || defaultData;
    const resetFormData = defaultData;
    return dispatch(
      cargoDialogSlice.actions.createNewCargo({
        initialFormData,
        resetFormData,
        isFromCache: !!previousFormData,
      })
    );
  };

// TODO: keep it like this? Or switch to error reporting async thunk
export const loadCargoSharedToStatus = createAsyncThunk(
  `${prefix}/get-shared-to-info`,
  async (cargoId: string) => {
    return await axios
      .get(`/api/cargos/${cargoId}/shared-to-status`)
      .then(getData)
      .then(tPromise.decode(cargoSharedToStatusT));
  }
);

export const loadShareFromOrgName = createErrorReportingAsyncThunk(
  `${prefix}/load-share-from-org-name`,
  async (cargoId: string) => {
    return await axios
      .get(`/api/cargos/${cargoId}/shared-from-status`)
      .then(getData)
      .then((x) => {
        return x.status;
      })
      .then(tPromise.decode(cargoSharedFromStatusT));
  }
);

export const cancelShareCargo = createErrorReportingAsyncThunk(
  `${prefix}/cancel-share-cargo`,
  async (input: { cargoId: string; contractId: string }) => {
    const { cargoId, contractId } = input;
    return axios.delete(
      `/api/cargos/${cargoId}/shared-to-status/${contractId}`
    );
  }
);

export const shareCargoWithSubcontractor = createErrorReportingAsyncThunk(
  `${prefix}/share-cargo-with-subcontractor`,
  async (
    input: {
      cargoId: string;
      contractId: string;
      contractorRef: string;
    },
    { dispatch }
  ) => {
    const { cargoId, contractorRef, contractId } = input;
    await axios.post(`/api/cargos/${cargoId}/share-to-subcontractor`, {
      subcontractingAgreementId: contractId,
      contractorRef,
    });
    dispatch(loadCargoSharedToStatus(cargoId));
  }
);

export const loadOrderSharedBy = createErrorReportingAsyncThunk(
  `${prefix}/load-order-shared-by`,
  async (orderId: string) => {
    return axios
      .get(`/api/orders/${orderId}/shared-by`)
      .then(getData)
      .then(tPromise.decode(t.strict({ sharedBy: t.string })));
  }
);

export const loadStopUnitsInfoFromSharedCargo = createErrorReportingAsyncThunk(
  `${prefix}/load-stop-units-info-from-shared-cargo`,
  async (orderId: string) => {
    return axios
      .get(`/api/orders/${orderId}/shared-from-stop-units-info`)
      .then(getData)
      .then(tPromise.decode(stopUniteTemplatesT));
  }
);

const cargoDialogSlice = createSlice({
  name: prefix,
  initialState,
  reducers: {
    createNewCargo: (
      state,
      action: PayloadAction<{
        initialFormData: CargoFormDataBase;
        resetFormData: CargoFormData | CargoFormDataBase;
        isFromCache: boolean;
      }>
    ) => {
      state.status = "CREATE";
      state.initialFormData = action.payload.initialFormData;
      state.previousFormData = undefined;
      state.initialFormDataFromCache = action.payload.isFromCache;
      state.resetFormData = action.payload.resetFormData;
      state.sharedToStatus = initialSharedToState;
    },
    close: (state, action: PayloadAction<CargoFormData | undefined>) => {
      state.initialFormData =
        state.status === "EDIT" ? undefined : state.initialFormData;
      state.previousFormData =
        state.status === "CREATE" ? action.payload : undefined;
      state.status = "CLOSED";
      state.sharedToStatus = initialSharedToState;
      state.remoteConflict = false;
    },
    // Mirrors submit.fulfilled's CREATE_AND_MAKE_COPY branch: the cargo(s)
    // were created directly on driver routes via startCreateCargoOnDriverRoutes
    // (a separate async job, not the submit thunk), so the modal reset has to
    // be triggered explicitly once that job completes.
    resetAfterCreateOnDriverRoutes: (
      state,
      action: PayloadAction<CargoFormData>
    ) => {
      state.status = "CREATE";
      const copy = createCargoCopy(action.payload);
      state.previousFormData = omit(copy, "ref");
      state.initialFormData = omit(copy, "ref");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submit.pending, (state) => {
        state.isSaving = true;
      })
      .addCase(submit.fulfilled, (state, { payload }) => {
        state.isSaving = false;
        if (payload.error) {
          // this means it is a caught error, the action result is handled where the action was dispatched
          return;
        }
        if (payload.submitType === "CREATE_AND_MAKE_COPY") {
          state.status = "CREATE";
          const copy = createCargoCopy(payload.requestFormData);
          state.previousFormData = omit(copy, "ref");
          state.initialFormData = omit(copy, "ref");
        } else {
          state.status = "CLOSED";
          state.previousFormData = undefined;
          state.initialFormData = undefined;
        }
      })
      .addCase(submit.rejected, (state) => {
        state.isSaving = false;
      })
      .addCase(openCargoFoundDeleted, (state, action) => {
        state.deletedCargo = action.payload;
      })
      .addCase(openCargoRevisionAdopted, (state, action) => {
        state.loadedDataRevisionNumber = action.payload;
      })
      .addCase(openCargoRemoteConflict, (state) => {
        state.remoteConflict = true;
      })
      .addCase(loadCargo.pending, (state, action) => {
        state.latestLoadCargoRequestId = action.meta.requestId;
      })
      .addCase(loadCargo.fulfilled, (state, action) => {
        if (!action.payload) return;
        if (action.meta.requestId !== state.latestLoadCargoRequestId) return;
        state.initialFormData = action.payload.data;
        state.resetFormData = action.payload.data;
        state.initialFormDataFromCache = false;
        state.loadedDataRevisionNumber = action.payload.data.revision;
        // A reload of the open cargo (after Ctrl+S) keeps a clash flagged
        // meanwhile; another cargo starts without one (DORA-441).
        if (state.editingCargoId !== action.payload.cargoId) {
          state.remoteConflict = false;
        }
        state.editingCargoId = action.payload.cargoId;
        state.status = "EDIT";
      })
      .addCase(loadCopyOfCargo.fulfilled, (state, action) => {
        state.initialFormData = action.payload.initialFormData;
        state.resetFormData = action.payload.resetFormData;
        state.initialFormDataFromCache = false;
        state.status = "CREATE";
      })
      .addCase(deleteCargo.fulfilled, (state) => {
        state.status = "CLOSED";
        state.previousFormData = undefined;
        state.initialFormData = undefined;
        state.resetFormData = undefined;
      })
      .addCase(loadAndUseClientCargoTemplate.fulfilled, (state, action) => {
        const { data, newDateAdjustment } = action.payload;
        const x = apiCargoToFormData({
          ...data.data,
          userId: data.data.createdByUserId,
          ref: "",
          createdAt: DateTime.utc(),
          operationInProgress: false,
          invoiceStatusLocked: false,
          // Not copied from the template's underlying prototype cargo - the
          // new cargo's reference to the template itself is set separately
          // at submit time from the selected-template context.
          clientCargoTemplateId: null,
          // Client cargo templates don't carry CMR config.
          cmrConfigs: [],
          // A new cargo - nothing calculated for it yet.
          distanceKm: null,
        });
        const firstPickupDate = DateTime.fromFormat(
          x.pickupList[0].date,
          "yyyy-MM-dd"
        );
        const diffToToday =
          DateTime.now().startOf("day").diff(firstPickupDate, "days").days +
          Number(newDateAdjustment || 0);
        const dropoffList = x.dropoffList || [];
        const [firstPickupStop, ...restPickups] = x.pickupList;
        firstPickupStop.date = DateTime.now()
          .startOf("day")
          .plus({ days: Number(newDateAdjustment) })
          .toFormat("yyyy-MM-dd");
        firstPickupStop.openingDate = firstPickupStop.openingDate
          ? (() => {
              const dt = DateTime.fromFormat(firstPickupStop.openingDate!, "yyyy-MM-dd").plus({ days: diffToToday });
              return dt.isValid ? dt.toFormat("yyyy-MM-dd") : null;
            })()
          : null;
        for (const stop of [...restPickups, ...dropoffList]) {
          stop.date = DateTime.fromFormat(stop.date, "yyyy-MM-dd")
            .plus({ days: diffToToday })
            .toFormat("yyyy-MM-dd");
          stop.openingDate = stop.openingDate
            ? (() => {
                const dt = DateTime.fromFormat(stop.openingDate!, "yyyy-MM-dd").plus({ days: diffToToday });
                return dt.isValid ? dt.toFormat("yyyy-MM-dd") : null;
              })()
            : null;
        }
        const processedData: CargoFormData = {
          // The template's prototype cargo id must not become the new cargo's
          // id: the dialog would load that cargo's splits/documents/events,
          // which 404 once the template (and its prototype cargo) is deleted.
          ...omit(x, "id"),
          dropoffList,
        };
        state.initialFormData = processedData;
        // state.resetFormData = processedData;
        state.initialFormDataFromCache = false;
        state.sharedToStatus = initialSharedToState;
      })
      .addCase(loadCargoSharedToStatus.fulfilled, (state, action) => {
        state.sharedToStatus = action.payload;
      });
  },
});

export const { close, resetAfterCreateOnDriverRoutes } =
  cargoDialogSlice.actions;
export default cargoDialogSlice.reducer;
